import { useMemo, useRef, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Checkbox,
  FilterChip,
  Pagination,
  SearchBar,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRoot,
  TableRow,
  TableToolbar,
} from '@firstbite/ui'
import useApiService from 'services/api'
import { DoorProxyForDealsSelection } from 'models/door_proxies_for_deals_selection'
import {
  addPageToSelection,
  chunkIds,
  computeTotalPages,
  doorLabel,
  getPageSelectionState,
  removePageFromSelection,
  toggleSelection,
} from './selectLocationsPanel.helpers'

// The doors-for-selection endpoint uses a fixed PageNumberPagination page size
// of 100 with no page-size override param, so this MUST match the server (or the
// page count and header "select all on page" go out of sync). The table scrolls
// internally (max-h-80) rather than shrinking the page.
const PAGE_SIZE = 100

interface SelectLocationsPanelFbuiProps {
  chainProxyId: number
  /** Total doors on the company — used to floor pagination / all-selected math. */
  companyTotalDoors: number
  selectedIds: number[]
  /** Called with the next selection whenever a door is toggled or chips cleared. */
  onSelectedIdsChange: (ids: number[]) => void
}

/**
 * The @firstbite/ui door picker for the contact modal's step 2 (ds2026 only).
 * Replaces the shared app `DataTable` two-tab picker with an fb-ui `Table`:
 * selected doors ride on top as removable `FilterChip`s, a `SearchBar` filters
 * the list, and each row's `Checkbox` toggles membership immediately. Server-
 * side paginated via the same `doors-for-selection` endpoint the deal modals
 * use.
 *
 * Iteration 1 intentionally drops the `DealsFilterset` advanced filters and the
 * "select all N across pages" affordance (step 1's "Entire company" radio covers
 * whole-company scope). Filters are additive — they slot into the `TableToolbar`
 * `actions` and become extra query params — so re-adding them later is
 * non-breaking.
 */
export const SelectLocationsPanelFbui = ({
  chainProxyId,
  companyTotalDoors,
  selectedIds,
  onSelectedIdsChange,
}: SelectLocationsPanelFbuiProps) => {
  const api = useApiService()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  // Labels for chips accumulate as rows are toggled on (we already hold the door
  // data at that point) — no extra fetch per selection.
  const [labelMap, setLabelMap] = useState<Record<number, string>>({})

  const applySearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleSearchInput = (value: string) => {
    setSearchInput(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => applySearch(value), 350)
  }

  // Main list (all doors for the company, server-paginated + searched).
  const { data, isFetching } = useQuery({
    queryKey: ['fbui-door-picker', chainProxyId, page, search],
    queryFn: ({ signal }) =>
      api.getDoorsForSelection(
        chainProxyId,
        { page, limit: PAGE_SIZE, search: search || undefined },
        {},
        signal
      ),
    placeholderData: keepPreviousData,
  })

  const rows = data?.results ?? []
  const totalCount = data?.count ?? 0
  const totalPages = computeTotalPages(totalCount, PAGE_SIZE)

  // Edit flow: doors selected before this panel mounted need labels for their
  // chips. Fetch them once for the mount-time selection (stable key), then read
  // labels from either source when rendering chips. The endpoint caps a single
  // response at PAGE_SIZE (server-fixed, no override), so chunk the id lookup
  // into PAGE_SIZE-sized pages and merge — otherwise a >PAGE_SIZE pre-selection
  // (edit flow on a large chain) would leave the overflow chips unlabeled.
  const [initialIds] = useState(selectedIds)
  const { data: initialSelected } = useQuery({
    queryKey: ['fbui-door-picker-selected', chainProxyId, initialIds],
    queryFn: async ({ signal }) => {
      const pages = await Promise.all(
        chunkIds(initialIds, PAGE_SIZE).map((ids) =>
          api.getDoorsForSelection(
            chainProxyId,
            { limit: PAGE_SIZE },
            { ids },
            signal
          )
        )
      )
      return pages.flatMap((p) => p.results)
    },
    enabled: initialIds.length > 0,
  })

  const chipLabelById = useMemo(() => {
    const map: Record<number, string> = { ...labelMap }
    for (const door of initialSelected ?? []) {
      if (map[door.id] == null) map[door.id] = doorLabel(door)
    }
    return map
  }, [labelMap, initialSelected])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const removeDoor = (id: number) =>
    onSelectedIdsChange(selectedIds.filter((x) => x !== id))

  const toggleDoor = (door: DoorProxyForDealsSelection) => {
    // Capture the label as we toggle on (we hold the door data now — avoids a
    // per-selection fetch); the id-set transform itself is the pure helper.
    if (!selectedSet.has(door.id)) {
      setLabelMap((prev) => ({ ...prev, [door.id]: doorLabel(door) }))
    }
    onSelectedIdsChange(toggleSelection(selectedIds, door.id))
  }

  // Header checkbox toggles just the doors on the current page.
  const pageIds = rows.map((d) => d.id)
  const pageState = getPageSelectionState(selectedIds, pageIds)

  const togglePage = () => {
    if (pageState === 'all') {
      onSelectedIdsChange(removePageFromSelection(selectedIds, pageIds))
    } else {
      setLabelMap((prev) => {
        const next = { ...prev }
        for (const door of rows) next[door.id] = doorLabel(door)
        return next
      })
      onSelectedIdsChange(addPageToSelection(selectedIds, pageIds))
    }
  }

  const clearAll = () => onSelectedIdsChange([])

  return (
    <div className="flex flex-col gap-3">
      {selectedIds.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-fbui-gray-200 bg-fbui-gray-50 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-fbui-gray-700">
              {selectedIds.length} of {companyTotalDoors} selected
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-sm font-medium text-fbui-basil-700 outline-none transition-colors hover:text-fbui-basil-800 focus-visible:underline"
            >
              Clear all
            </button>
          </div>
          <div className="fbui-scroll flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
            {selectedIds.map((id) => (
              <FilterChip
                key={id}
                value={chipLabelById[id] ?? `Location #${id}`}
                onRemove={() => removeDoor(id)}
                removeLabel="Remove location"
              />
            ))}
          </div>
        </div>
      )}

      <TableRoot
        size="sm"
        stickyHeader
        className="max-h-80"
        header={
          <TableToolbar
            // Uniform p-2 matches the checkbox `control` column's padding so the
            // search bar's left edge aligns with the checkbox and the toolbar
            // padding reads evenly on all four sides (default is px-3/py-2).
            className="p-2"
            search={
              <SearchBar
                size="sm"
                placeholder="Search locations by name or address…"
                value={searchInput}
                onValueChange={handleSearchInput}
                onSearch={applySearch}
                clearable
                onClear={() => {
                  setSearchInput('')
                  applySearch('')
                }}
              />
            }
          />
        }
        footer={
          totalPages > 1 ? (
            <TableFooter
              leading={`${totalCount} location${totalCount === 1 ? '' : 's'}`}
            >
              <Pagination
                size="sm"
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </TableFooter>
          ) : undefined
        }
      >
        <TableHeader>
          <TableRow>
            {/* `control` = hugging checkbox column (Name below carries w-full to
                absorb the freed slack). */}
            <TableHead pin="left" control>
              <Checkbox
                size="sm"
                aria-label="Select all locations on this page"
                checked={pageState === 'all'}
                indeterminate={pageState === 'some'}
                onChange={togglePage}
              />
            </TableHead>
            <TableHead>Location Name</TableHead>
            {/* Address absorbs the table's slack so the other columns (esp. the
                checkbox) collapse to their content width in this auto-layout table. */}
            <TableHead className="w-full">Address</TableHead>
            <TableHead>City</TableHead>
            <TableHead>State</TableHead>
            <TableHead truncate={false}>ZIP</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                truncate={false}
                className="py-8 text-center text-fbui-gray-500"
              >
                {isFetching
                  ? 'Loading locations…'
                  : search
                    ? 'No locations match your search.'
                    : 'No locations found.'}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((door) => {
              const isSelected = selectedSet.has(door.id)
              return (
                <TableRow
                  key={door.id}
                  selected={isSelected}
                  className="cursor-pointer"
                  onClick={() => toggleDoor(door)}
                >
                  <TableCell pin="left" control>
                    <Checkbox
                      size="sm"
                      aria-label={`Select ${doorLabel(door)}`}
                      checked={isSelected}
                      onChange={() => toggleDoor(door)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{door.name}</TableCell>
                  <TableCell className="w-full">
                    {door.address || '—'}
                  </TableCell>
                  <TableCell>{door.city || '—'}</TableCell>
                  <TableCell>{door.state || '—'}</TableCell>
                  <TableCell truncate={false}>{door.zipcode || '—'}</TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </TableRoot>
    </div>
  )
}
